import { useEffect, useState } from 'react';
import { fetchArticle, bumpView } from '../api/community';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SmartImg from "../components/SmartImg";

export default function CommunityArticle() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    fetchArticle(slug).then(data => {
      setA(data);
      bumpView(data.id);
    });
  }, [slug]);

  // back-to-top visibility (mobile & desktop)
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!a) {
    return <div className="text-center text-slate-400 py-6">Loading…</div>;
  }

  const metaTitle = (a.metaTitle || a.title).slice(0, 60);
  const metaDescription = (a.metaDescription || a.excerpt || '').slice(0, 160);

  const canonicalUrl = `https://www.hey-lets-travel.com/community/${a.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": metaTitle,
    "description": metaDescription,
    "articleSection": a.category, // EVENT | DJ | AFFILIATE
    "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalUrl },
    "datePublished": a.publishedAt,
    "dateModified": a.updatedAt || a.publishedAt,
    "author": { "@type": "Organization", "name": "Hey Let’s Travel" },
    "publisher": {
      "@type": "Organization",
      "name": "Hey Let’s Travel",
      "logo": { "@type": "ImageObject", "url": "https://www.hey-lets-travel.com/logo.png" }
    },
    "image": a.thumbnailUrl ? [a.thumbnailUrl] : []
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {a.keywords && <meta name="keywords" content={a.keywords} />}

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        {a.thumbnailUrl && <meta property="og:image" content={a.thumbnailUrl} />}
        <meta property="og:url" content={canonicalUrl} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {a.thumbnailUrl && <meta name="twitter:image" content={a.thumbnailUrl} />}
        {/* optional but fine to include */}
        <meta name="twitter:url" content={canonicalUrl} />

        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <article className="page">
        {/* Back to community (top-left) */}
        <div className="mb-3">
          <Link to="/community" className="btn-secondary">← Back to Community</Link>
        </div>

        <header className="text-center mb-4">
          <span className="badge">{a.category}</span>
          <h1 className="mt-2 font-display text-3xl md:text-4xl text-white leading-tight break-words [overflow-wrap:anywhere]">
            {a.title}
          </h1>
          {a.thumbnailUrl && (
            <figure className="mt-3 mx-auto w-full max-w-[1280px]">
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                <SmartImg
                  src={a.thumbnailUrl}
                  alt={a.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  width={1280}
                  height={720}
                />
              </div>
            </figure>
          )}
        </header>

        {/* Body — add robust wrapping so single long tokens can’t blow layout */}
        <section
          className="
            card text-slate-200 leading-7 break-words [overflow-wrap:anywhere]
            [&>h2]:text-white [&>h2]:text-2xl [&>h2]:mt-4 [&>h2]:mb-2
            [&>h3]:text-white [&>h3]:text-xl  [&>h3]:mt-4 [&>h3]:mb-2
            [&>p]:mb-4
            [&_img]:rounded-xl [&_img]:border [&_img]:border-white/10 [&_img]:bg-white/5
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul>li]:mb-2
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol>li]:mb-2
          "
          dangerouslySetInnerHTML={{ __html: a.bodyHtml }}
        />

        {a.category === 'EVENT' && a.eventId && (
          <p className="mt-6 text-center">
            <Link to={`/purchase/${a.eventId}`} className="btn-primary">
              Purchase tickets →
            </Link>
          </p>
        )}
      </article>

      {/* Back to top (desktop + mobile) */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-40 btn-primary shadow-lg"
          aria-label="Back to top"
        >
          ↑ Top
        </button>
      )}
    </>
  );
}
